import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Office } from '../../../models/locations';
import { BaseComponent } from '../../../components/base/base.component';
import { AgilePayrollStatuses, PayrollStatuses } from '../../../models/payroll';
import { PayrollFrequency, PayrollInputMethods } from '../../../models/payroll-info';
import {
  PayrollTaskSearchConfig,
  SubTaskCompletion,
  Task,
  TaskPriorities,
  TaskSearchConfig,
  TaskStatuses
} from '../../../models/task';
import { CompanySubTaskGroup } from '../../../models/company-subtask';
import { CamelCaseToTitleCasePipe } from '../../../pipes/camel-case-to-title-case.pipe';
import { AuthService } from '../../../services/auth.service';
import { IdNamePair } from '../../../models/id-name-pair';

@Component({
  template: '',
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('0.25s ease-out', style({ height: 'unset', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: 'unset', opacity: 1 }),
        animate('0.1s ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ],
  standalone: false
})
export class BaseTaskListComponent extends BaseComponent implements OnChanges, OnInit {
  @Input() clearSelTaskEvent: Observable<void>;
  @Input() expandedTasks: {};
  @Input() csrOfficeMap: Office[];
  @Input() tasks: any[];
  @Input() searchConfig: TaskSearchConfig | PayrollTaskSearchConfig;
  @Input() totalTaskCounts = {
    payroll: { open: 0, closed: 0 },
    other: { open: 0, closed: 0 }
  };
  @Input() taskTypes: IdNamePair[];
  @Input() viewMembers: any[];

  @Output() onEditTask: EventEmitter<Task> = new EventEmitter();
  @Output() onGetTasks: EventEmitter<any> = new EventEmitter();
  @Output() onTaskSelect: EventEmitter<any> = new EventEmitter();
  @Output() onToggleExpTasks: EventEmitter<number> = new EventEmitter();
  @Output() onToggleSubTaskComplete: EventEmitter<any> = new EventEmitter();

  private csrNameMap = {};
  private clearSelTaskSubscription: Subscription;

  ngOnInit() {
    this.clearSelTaskSubscription = this.clearSelTaskEvent.subscribe(() => {
      this.selTasks = {};
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // check if we have a csrOfficeMap; if so, build a lookup table for name translations
    // if not, clear our lookup table
    if (changes.csrOfficeMap) {
      // if we're getting a value, build a lookup map
      if (changes.csrOfficeMap.currentValue) {
        changes.csrOfficeMap.currentValue
          .flatMap(o => o.CSRs)
          .forEach(
            csr =>
              (this.csrNameMap[csr.userName.toLowerCase()] = `${csr.firstName} ${csr.lastName}`)
          );
      }
      // no value?  clear the lookup map
      else {
        this.csrNameMap = {};
      }
    }
  }

  public searchSubject = new Subject<void>();
  public selTasks = {};
  // tracked separately due to the multi select checkbox setup
  public payrollMethodFilters = {};
  public payrollStatusFilters = {};
  public taskStatusFilters = {};

  constructor(
    public authService: AuthService,
    public camelToTitleCasePipe: CamelCaseToTitleCasePipe
  ) {
    super();

    // ensure that column filter searches are debounced
    this.searchSubject.pipe(debounceTime(500)).subscribe(() => {
      this.onGetTasks.emit(this.searchConfig);
    });
  }

  get agilePayrollStatuses() {
    return AgilePayrollStatuses;
  }
  get payrollInputMethods() {
    return PayrollInputMethods;
  }
  get payrollFrequencies() {
    return PayrollFrequency;
  }
  get payrollStatuses() {
    return PayrollStatuses;
  }
  get subTaskGroups() {
    return CompanySubTaskGroup;
  }
  get taskPriorities() {
    return TaskPriorities;
  }
  get taskStatuses() {
    return TaskStatuses;
  }

  public editTask(task: Task) {
    this.onEditTask.emit(task);
  }

  public filterBy(taskContainer, debounce: boolean = false) {
    // when filtering the table, scroll it back to the top
    if (taskContainer && taskContainer.scrollTop != undefined) {
      taskContainer.scrollTop = 0;
    }

    // when filtering, clear selected tasks and go back to page 1
    this.selTasks = {};
    this.searchConfig.PageNumber = 1;

    // turn assignee column filter into an array.
    let assignee: any = this.searchConfig.Assignees;
    if (assignee && typeof assignee === 'string') {
      this.searchConfig.Assignees = [assignee];
    }

    // if the assignee column filter is cleared,
    // it needs to be set with the collection of view members.
    // which is a list of all the CSRs in the current task view
    // e.g. My Tasks, My Office Tasks
    if (!this.searchConfig.Assignees[0]) {
      this.searchConfig.Assignees = this.viewMembers.map(x => x.userName);
    }

    if (!this.searchConfig.TaskTypes[0]) {
      this.searchConfig.TaskTypes = [];
    }

    const payrollSearchConfig = this.searchConfig as PayrollTaskSearchConfig;

    payrollSearchConfig.ReceivedBy = [];
    Object.keys(this.payrollMethodFilters).forEach(key => {
      if (this.payrollMethodFilters[key]) {
        payrollSearchConfig.ReceivedBy.push(this.payrollInputMethods[key]);
      }
    });

    payrollSearchConfig.PayrollStatuses = [];
    Object.keys(this.payrollStatusFilters).forEach(key => {
      if (this.payrollStatusFilters[key]) {
        payrollSearchConfig.PayrollStatuses.push(key);
      }
    });

    this.searchConfig.Statuses = [];
    Object.keys(this.taskStatusFilters).forEach(key => {
      if (this.taskStatusFilters[key]) {
        this.searchConfig.Statuses.push(+key);
      }
    });

    if (debounce) {
      this.searchSubject.next();
    } else {
      this.onGetTasks.emit(this.searchConfig);
    }
  }

  public getPayrollStatusFilterText() {
    let text = [];

    const config = this.searchConfig as PayrollTaskSearchConfig;

    if (config.PayrollStatuses) {
      config.PayrollStatuses.forEach(key => {
        text.push(key);
      });
    }

    return text.length ? text.join(', ') : 'Filter...';
  }

  public getStatusFilterText() {
    let text = [];

    if (this.searchConfig.Statuses) {
      this.searchConfig.Statuses.forEach(key => {
        text.push(this.camelToTitleCasePipe.transform(TaskStatuses[key]));
      });
    }

    return text.length ? text.join(', ') : 'Filter...';
  }

  public getMethodFilterText() {
    let text = [];

    const config = this.searchConfig as PayrollTaskSearchConfig;

    if (config.ReceivedBy) {
      config.ReceivedBy.forEach(key => text.push(key));
    }

    return text.length ? text.join(', ') : 'Filter...';
  }

  public getTaskAssigneeName(userName) {
    const normalizedUserName = userName.toLowerCase();
    if (this.csrNameMap[normalizedUserName]) return this.csrNameMap[normalizedUserName];
  }

  public isPayrollTaskType(type: string) {
    return type && type.includes('Payroll');
  }

  // user has scrolled towards the bottom of the table
  public onScroll() {
    // check to see if another page of data is available
    if (this.searchConfig.PageNumber < this.searchConfig.TotalPages) {
      // fetch the next page of data
      // build a new config object so the new page change can be picked up
      const config = { ...this.searchConfig };
      config.PageNumber++;

      this.onGetTasks.emit(config);
    }
  }

  public sortTasksBy(event, column, taskContainer) {
    const $table = event.target.closest('table');
    // check to see if same column or new column was clicked
    if (this.searchConfig.SortKey !== column) {
      this.removeThClass($table.getAttribute('id'), 'asc');
      this.removeThClass($table.getAttribute('id'), 'desc');
      this.searchConfig.SortKey = column;
    }

    // default to ascending order
    if (!this.searchConfig.SortDirection || this.searchConfig.SortDirection === 'desc') {
      this.searchConfig.SortDirection = 'asc';
      event.target.classList.add('asc');
      event.target.classList.remove('desc');
    } else {
      this.searchConfig.SortDirection = 'desc';
      event.target.classList.add('desc');
      event.target.classList.remove('asc');
    }

    // for infinite scroll to work properly,
    // the table needs to be scrolled back to the top and reset to page 1
    if (taskContainer && taskContainer.scrollTop != undefined) {
      taskContainer.scrollTop = 0;
    }

    this.searchConfig.PageNumber = 1;

    this.onGetTasks.emit(this.searchConfig);
  }

  public toggleExpandedTasks(taskId: number) {
    this.onToggleExpTasks.emit(taskId);
  }

  public toggleSelectedTasks() {
    Object.keys(this.selTasks).forEach(key => {
      // if checkbox is checked, set value to task object
      if (this.selTasks[key]) {
        this.selTasks[key] = this.tasks.find(x => x.Id == +key);
      }
    });

    this.onTaskSelect.emit(this.selTasks);
  }

  public toggleSubTaskCompletion(taskId: number, subTaskId: number, completed: boolean) {
    this.onToggleSubTaskComplete.emit(new SubTaskCompletion(taskId, subTaskId, completed));
  }

  // #endregion
}
